import { supabase } from '../lib/supabase-auth';

export interface ClientInvitation {
  id: string;
  email: string;
  full_name: string;
  party_id?: string;
  invited_by: string;
  token: string;
  expires_at: string;
  accepted_at?: string;
  status: 'pending' | 'accepted' | 'expired' | 'cancelled';
  created_at: string;
  updated_at: string;
}

export interface SendInvitationData {
  email: string;
  fullName: string;
  partyId?: string;
}

// Generate a secure random token
const generateToken = (): string => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

export const invitationService = {
  // Send a client invitation
  async sendInvitation(data: SendInvitationData): Promise<{ success: boolean; error?: string; invitation?: ClientInvitation }> {
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { success: false, error: 'User not authenticated' };
      }

      // Get platform user
      const { data: platformUser } = await supabase
        .from('platform_users')
        .select('id')
        .eq('auth_user_id', user.id)
        .single();

      if (!platformUser) {
        return { success: false, error: 'Platform user not found' };
      }

      // Generate token and expiry
      const token = generateToken();
      const expiresAt = new Date();
      expiresAt.setHours(expiresAt.getHours() + 24); // 24 hour expiry

      // Create invitation record
      const invitationRecord: any = {
        email: data.email.toLowerCase(),
        full_name: data.fullName,
        party_id: data.partyId,
        invited_by: platformUser.id,
        token,
        expires_at: expiresAt.toISOString(),
        status: 'pending' as const,
        email_sent_at: new Date().toISOString()
      };
      
      const { error: inviteError } = await supabase
        .from('client_invitations')
        .insert(invitationRecord);

      if (inviteError) {
        if (import.meta.env.DEV) {

          if (import.meta.env.DEV) {
            console.error('Error creating invitation:', inviteError);
          }

        }
        return { success: false, error: 'Failed to create invitation' };
      }
      
      // Fetch the created invitation
      const { data: invitation, error: fetchError } = await supabase
        .from('client_invitations')
        .select('*')
        .eq('token', token)
        .single();
      
      if (fetchError || !invitation) {
        if (import.meta.env.DEV) {

          if (import.meta.env.DEV) {
            console.error('Error fetching invitation:', fetchError);
          }

        }
        return { success: false, error: 'Failed to fetch created invitation' };
      }

      // Send invitation email via API
      const apiUrl = import.meta.env.DEV 
        ? '/api/invite-client'  // In dev, Vite will proxy to Vercel
        : '/api/invite-client'; // In prod, relative URL works
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: data.email,
          fullName: data.fullName,
          token,
          invitedBy: user.email
        }),
      });

      if (!response.ok) {
        // If email fails, update invitation status
        await supabase
          .from('client_invitations')
          .eq('id', invitation.id)
          .update({ status: 'cancelled' });
        
        return { success: false, error: 'Failed to send invitation email' };
      }

      return { success: true, invitation };
    } catch (error) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Invitation error:', error);
        }

      }
      return { success: false, error: 'An unexpected error occurred' };
    }
  },

  // Verify invitation token
  async verifyInvitation(token: string): Promise<{ valid: boolean; invitation?: ClientInvitation; error?: string }> {
    try {
      const { data: invitation, error } = await supabase
        .from('client_invitations')
        .select('*')
        .eq('token', token)
        .single();

      if (error || !invitation) {
        return { valid: false, error: 'Invalid invitation token' };
      }

      // Check if expired
      if (new Date(invitation.expires_at) < new Date()) {
        // Update status to expired
        await supabase
          .from('client_invitations')
          .eq('id', invitation.id)
          .update({ status: 'expired' });
        
        return { valid: false, error: 'This invitation has expired' };
      }

      // Check if already accepted
      if (invitation.status === 'accepted') {
        return { valid: false, error: 'This invitation has already been used' };
      }

      if (invitation.status === 'cancelled') {
        return { valid: false, error: 'This invitation has been cancelled' };
      }

      return { valid: true, invitation };
    } catch (error) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Verify invitation error:', error);
        }

      }
      return { valid: false, error: 'Failed to verify invitation' };
    }
  },

  // Accept invitation and create account
  async acceptInvitation(token: string, password: string): Promise<{ success: boolean; error?: string }> {
    try {
      // Use server-side API to handle user creation/update
      const apiUrl = import.meta.env.DEV 
        ? '/api/accept-invitation'
        : '/api/accept-invitation';
        
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          password
        }),
      });

      const result = await response.json();
      
      if (!response.ok) {
        return { success: false, error: result.error || 'Failed to accept invitation' };
      }

      return { success: true };
    } catch (error) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Accept invitation error:', error);
        }

      }
      return { success: false, error: 'An unexpected error occurred' };
    }
  },

  // OLD CODE - REPLACED WITH SERVER-SIDE API CALL
  async acceptInvitationOld(token: string, password: string): Promise<{ success: boolean; error?: string }> {
    try {
      // First verify the invitation
      const { valid, invitation, error: verifyError } = await this.verifyInvitation(token);
      
      if (!valid || !invitation) {
        return { success: false, error: verifyError || 'Invalid invitation' };
      }

      // Try to create auth user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: invitation.email,
        password,
        options: {
          data: {
            full_name: invitation.full_name
          }
        }
      });

      let authUserId: string;

      if (authError) {
        if (import.meta.env.DEV) {

          if (import.meta.env.DEV) {
            console.error('Auth error:', authError);
          }

        }
        
        // Check if error is because user already exists
        if (authError.message?.toLowerCase().includes('already registered') || 
            authError.message?.toLowerCase().includes('user already exists')) {
          
          // Try to sign in to get the user ID
          const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
            email: invitation.email,
            password
          });
          
          if (signInError) {
            // If sign in fails, they need to reset password
            return { 
              success: false, 
              error: 'This email is already registered. Please reset your password first, then try the invitation link again.' 
            };
          }
          
          if (signInData.user) {
            authUserId = signInData.user.id;
          } else {
            return { success: false, error: 'Failed to authenticate user' };
          }
        } else {
          return { success: false, error: authError.message || 'Failed to create account' };
        }
      } else if (authData.user) {
        authUserId = authData.user.id;
      } else {
        return { success: false, error: 'Failed to create user' };
      }

      // Check if client user already exists
      const { data: existingClientUser } = await supabase
        .from('client_users')
        .select('id')
        .eq('auth_user_id', authUserId)
        .maybeSingle();

      if (!existingClientUser) {
        // Create client user record only if it doesn't exist
        const { error: clientError } = await supabase
          .from('client_users')
          .insert({
            auth_user_id: authUserId,
            email: invitation.email,
            full_name: invitation.full_name,
            party_id: invitation.party_id,
            is_primary: true,
            is_active: true,
            can_view_documents: true,
            can_message_advisor: true,
            can_view_reports: true,
            can_update_profile: true,
            created_by: invitation.invited_by
          });

        if (clientError) {
          if (import.meta.env.DEV) {

            if (import.meta.env.DEV) {
              console.error('Client user error:', clientError);
            }

          }
          // TODO: Consider cleanup of auth user
          return { success: false, error: 'Failed to create client profile' };
        }
      } else {
        // Update existing client user to link with party if needed
        if (invitation.party_id) {
          await supabase
            .from('client_users')
            .update({ party_id: invitation.party_id })
            .eq('id', existingClientUser.id);
        }
      }


      // Update invitation status
      await supabase
        .from('client_invitations')
        .eq('id', invitation.id)
        .update({
          status: 'accepted',
          accepted_at: new Date().toISOString()
        });

      return { success: true };
    } catch (error) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Accept invitation error:', error);
        }

      }
      return { success: false, error: 'An unexpected error occurred' };
    }
  },

  // Get pending invitations for current user
  async getPendingInvitations(): Promise<{ data?: ClientInvitation[]; error?: string }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { error: 'User not authenticated' };
      }

      // Get platform user
      const { data: platformUser } = await supabase
        .from('platform_users')
        .select('id')
        .eq('auth_user_id', user.id)
        .single();

      if (!platformUser) {
        return { error: 'Platform user not found' };
      }

      const { data, error } = await supabase
        .from('client_invitations')
        .select('*')
        .eq('invited_by', platformUser.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) {
        if (import.meta.env.DEV) {

          if (import.meta.env.DEV) {
            console.error('Error fetching invitations:', error);
          }

        }
        return { error: 'Failed to fetch invitations' };
      }

      return { data };
    } catch (error) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Get invitations error:', error);
        }

      }
      return { error: 'An unexpected error occurred' };
    }
  },

  // Resend invitation
  async resendInvitation(invitationId: string): Promise<{ success: boolean; error?: string }> {
    try {
      // Get the invitation
      const { data: invitation, error: fetchError } = await supabase
        .from('client_invitations')
        .select('*')
        .eq('id', invitationId)
        .single();

      if (fetchError || !invitation) {
        return { success: false, error: 'Invitation not found' };
      }

      // Generate new token and expiry
      const token = generateToken();
      const expiresAt = new Date();
      expiresAt.setHours(expiresAt.getHours() + 24);

      // Update invitation
      const { error: updateError } = await supabase
        .from('client_invitations')
        .eq('id', invitationId)
        .update({
          token,
          expires_at: expiresAt.toISOString(),
          status: 'pending',
          email_sent_at: new Date().toISOString(),
          resent_count: (invitation.resent_count || 0) + 1,
          last_resent_at: new Date().toISOString()
        });

      if (updateError) {
        return { success: false, error: 'Failed to update invitation' };
      }

      // Send email
      const apiUrl = import.meta.env.DEV 
        ? '/api/invite-client'  // In dev, Vite will proxy to Vercel
        : '/api/invite-client'; // In prod, relative URL works
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: invitation.email,
          fullName: invitation.full_name,
          token,
          invitedBy: invitation.invited_by
        }),
      });

      if (!response.ok) {
        return { success: false, error: 'Failed to send invitation email' };
      }

      return { success: true };
    } catch (error) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Resend invitation error:', error);
        }

      }
      return { success: false, error: 'An unexpected error occurred' };
    }
  },

  // Cancel invitation
  async cancelInvitation(invitationId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('client_invitations')
        .eq('id', invitationId)
        .update({ status: 'cancelled' });

      if (error) {
        return { success: false, error: 'Failed to cancel invitation' };
      }

      return { success: true };
    } catch (error) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Cancel invitation error:', error);
        }

      }
      return { success: false, error: 'An unexpected error occurred' };
    }
  }
};